import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Settings } from 'lucide-react';
import { useDaggerheartData } from '../context/DataContext';

const CampaignCreator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    campaignFrame: 'core',
    gmNotes: '',
    // Session & Gameplay Settings
    autoArchiveNotes: true,
    playersCanViewSheets: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const allData = useDaggerheartData();
  const frames = allData?.campaignFrames || {};
  const availableFrames = [
    { value: 'core', label: 'Core Rules' },
    ...Object.entries(frames)
      .filter(([id]) => id !== 'core')
      .map(([id, data]) => ({ value: id, label: data.name || id }))
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Campaign name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const campaignData = {
        name: formData.name.trim(),
        campaignFrame: formData.campaignFrame,
        gmNotes: formData.gmNotes.trim(),
        gmId: user.uid,
        gmName: user.displayName || user.email || 'Game Master',
        players: [],
        settings: {
          autoArchiveNotes: formData.autoArchiveNotes,
          playersCanViewSheets: formData.playersCanViewSheets
        },
        gmMode: {
          enabled: true,
          secretDCPreference: 'secret',
          dcPresets: { easy: 12, moderate: 14, hard: 16, veryHard: 18 },
          rollVisibility: 'outcome-only',
          autoResultMessages: true
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'campaigns'), campaignData);

      // Navigate to the new campaign with state to trigger welcome modal
      navigate(`/campaign/${docRef.id}`, {
        state: { justCreated: true, campaignName: formData.name.trim() }
      });
      
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError('Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212]">
      <header className="bg-[#1E1E1E] border-b border-gray-700 p-4 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-100 font-serif">
            Create New Campaign
          </h1>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-md hover:bg-gray-700 border border-gray-600 transition-colors"
          >
            Back to Main Menu
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        <div className="bg-gradient-to-br from-gray-900 to-black border border-red-700 rounded-lg shadow-2xl p-8">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-600 text-red-400 rounded-md">
                {error}
              </div>
            )}

            {/* Basic Campaign Info Section */}
            <div className="space-y-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-100">Campaign Details</h3>

              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  Campaign Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-[#232323] border border-gray-600 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                  placeholder="Enter campaign name"
                />
              </div>

              <div>
                <label
                  htmlFor="campaignFrame"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  Campaign Frame
                </label>
                <select
                  id="campaignFrame"
                  name="campaignFrame"
                  value={formData.campaignFrame}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-[#232323] border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                >
                  {availableFrames.map(frame => (
                    <option key={frame.value} value={frame.value}>
                      {frame.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Players will need characters that match this campaign frame to join.
                </p>
              </div>

              <div>
                <label
                  htmlFor="gmNotes"
                  className="block text-sm font-medium text-gray-300 mb-2"
                >
                  GM Notes (Optional)
                </label>
                <textarea
                  id="gmNotes"
                  name="gmNotes"
                  value={formData.gmNotes}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 bg-[#232323] border border-gray-600 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                  placeholder="Add any notes or information about your campaign..."
                />
              </div>
            </div>

            {/* Session & Gameplay Settings */}
            <div className="border-t border-gray-700 pt-8 mb-8">
              <h3 className="text-lg font-semibold text-gray-100 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-red-400" />
                Session & Gameplay Settings
              </h3>

              <div className="space-y-4">
                <div className="bg-[#232323] rounded-lg p-4 border border-gray-700">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-gray-200 font-medium">Auto-Archive Session Notes</span>
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically archive notes when starting a new session
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      name="autoArchiveNotes"
                      checked={formData.autoArchiveNotes}
                      onChange={handleInputChange}
                      className="ml-4 h-5 w-5 rounded text-red-600 bg-gray-700 border-gray-600 focus:ring-red-500"
                    />
                  </label>
                </div>

                <div className="bg-[#232323] rounded-lg p-4 border border-gray-700">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-gray-200 font-medium">Players Can View Each Other's Sheets</span>
                      <p className="text-xs text-gray-500 mt-1">
                        Allow players to view party member character sheets
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      name="playersCanViewSheets"
                      checked={formData.playersCanViewSheets}
                      onChange={handleInputChange}
                      className="ml-4 h-5 w-5 rounded text-red-600 bg-gray-700 border-gray-600 focus:ring-red-500"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-md hover:bg-gray-700 border border-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gray-800 text-gray-100 rounded-md hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </form>
        </div>
      </main>

    </div>
  );
};

export default CampaignCreator;
